import { GOLDEN_ENROLMENT_FEE_CENTS, SP_GOLDEN_MAX_BPS, SP_GOLDEN_MIN_BPS, SP_STANDARD_BPS, spLadder } from '@evi/shared';
import { FilmHero } from '@/components/FilmHero';
import { PARTNERS_APPLY_URL } from '@/lib/partnerLinks';
import styles from './SalesPartnerPage.module.css';

/** Horizontal crop (object-position X, percent) where the hero crops the
 *  landscape film's sides (phones and portrait tablets). 42 keeps the subject
 *  of every shot in a 390px phone (checked across the film: the van and the
 *  installer, the charger, the app, both close-ups). */
const HERO_FRAMING_X = 42;

/** The rates in words, from the shared ladders (sales comp change, 27 October 2026), so the page never restates a number.
 *  Prose reads short ("3.5%"); the Golden table reads as the terms publish it ("3.50%"). No earnings projections. */
const short = (bps: number) => `${Number((bps / 100).toFixed(2))}%`;
const GOLDEN_ROWS = spLadder('golden').map((r, i, all) => { const next = all[i + 1]; return { from: r.from, to: next ? next.from - 1 : null, rate: `${(r.rate_bps / 100).toFixed(2)}%` }; });
const GOLDEN_TOP_AT = Math.max(...GOLDEN_ROWS.map(r => r.from));
const GOLDEN_FEE = `$${(GOLDEN_ENROLMENT_FEE_CENTS / 100).toLocaleString('en-US')}`;

/** The Sales Partner program page (/sales-partners): the program film, the
 *  pitch, the rates. The application itself lives on evinitiative.com/partners
 *  (Warren, 8 Oct 2026: the better intake form), so every "See if I qualify"
 *  goes there in the same tab; this page has no form of its own. */
export function SalesPartnerPage() {
  return (
    <div className={styles.page}>
      {/* The program film (Warren, 9 Oct 2026): landscape 16:9, so FilmHero
          shows it whole on a wide screen; on a phone HERO_FRAMING_X picks the
          crop. */}
      <FilmHero
        video="/media/ugc/sales-partner-hero.mp4"
        poster="/media/ugc/sales-partner-hero-poster.jpg"
        orientation="landscape"
        framingX={HERO_FRAMING_X}
        framing={50}
        label="EVI Sales Partner Program"
      >
        <p className={styles.heroEyebrow}>EVI Sales Partner Program</p>
        <h1 className={styles.heroTitle}>Install once. Earn on every session. Forever.</h1>
      </FilmHero>

      <div className={styles.body}>
        <p className={styles.intro}>
          The only EV charging network that pays its partners for the life of every charger they
          deploy. Buy hardware at MSRP, keep your install margin, and earn a share of every
          pay-to-use session on the chargers you bring to the network, paid out through Stripe.
        </p>
        <a href={PARTNERS_APPLY_URL} className={`eviBtn eviBtnPrimary ${styles.introCta}`}>See if I qualify</a>

      <section className={styles.pitch}>
        <div className={styles.pitchCard}>
          <img className={styles.pitchIcon} src="/media/sp/msrp.webp" alt="" width={56} height={56} loading="lazy" />
          <h3>Buy at MSRP</h3>
          <p>Order the same commercial hardware at manufacturer list price. No membership, no markup to us.</p>
        </div>
        <div className={styles.pitchCard}>
          <img className={styles.pitchIcon} src="/media/sp/margin.webp" alt="" width={56} height={56} loading="lazy" />
          <h3>Your margin is yours</h3>
          <p>Quote the install and the hardware however you like. Whatever you charge your customer, you keep.</p>
        </div>
        <div className={styles.pitchCard}>
          <img className={styles.pitchIcon} src="/media/sp/earn-forever.webp" alt="" width={56} height={56} loading="lazy" />
          <h3>Earn on every session, forever</h3>
          <p>Once you qualify, every pay-to-use charger you deploy pays you a share of the platform fee on every session it runs, for as long as it runs.</p>
        </div>
      </section>

      <section className={styles.section}>
        <h2 className={styles.h2}>What you earn on sessions</h2>
        <p className={styles.sectionLead}>
          As a Standard Partner you earn {short(SP_STANDARD_BPS)} of every paid session on every
          qualifying charger once your 4th pay-to-use charger is live. Your first three chargers earn
          it too from then on, and every charger keeps the rate it launched with.
        </p>
        <h3 className={styles.h3}>Golden Partner</h3>
        <p className={styles.sectionLead}>
          For a one-time {GOLDEN_FEE}, Golden Partners skip the 4-charger start. You earn{' '}
          {short(SP_GOLDEN_MIN_BPS)}, rising to {short(SP_GOLDEN_MAX_BPS)} at {GOLDEN_TOP_AT} chargers, on the chargers you
          launch after enrolling.
        </p>
        <table className={styles.tiers}>
          <thead>
            <tr><th>Charger, by deployment order</th><th>Golden rate</th></tr>
          </thead>
          <tbody>
            {GOLDEN_ROWS.map(r => <tr key={r.from}><td>{r.to ? `${r.from} to ${r.to}` : `${r.from} and up`}</td><td>{r.rate}</td></tr>)}
          </tbody>
        </table>
        <p className={styles.fine}>
          Commission is paid from EVI's share of each pay-to-use session, never from the site host's
          share, and is paid through Stripe Connect. Free chargers do not count toward your
          deployment total.
        </p>
      </section>

      <section className={styles.section}>
        <h2 className={styles.h2}>Staying active</h2>
        <p className={styles.sectionLead}>
          To keep your partner status active, deploy at least 8 new pay-to-use chargers every 12
          months after you qualify. It keeps the program for partners who are actively building the
          network. We will always talk it through with you first.
        </p>
      </section>
      </div>
    </div>
  );
}

